// Module
import type { RouteObject } from "react-router-dom";

// Constant
import { Routes } from "constants/global";

// Components
import Login from "containers/Login/LoginPage";
import Alert from "containers/Alert/AlertPage";
import NewUser from "containers/NewUser/NewUserPage";
import Home from "containers/Home/Home";
import NewGroup from "containers/NewGroup/NewGroupPage";
import RoleManagementPage from "containers/RoleManagement/RoleManagementPage";
import ViewEditUserPage from "containers/ViewEditUser/ViewEditUserPage";
import RouteGuard from "app/hooks/RouteGuard";
import EditGroupPage from "containers/EditGroup/EditGroupPage";
import MonthlyDeclarationPage from "containers/MonthlyDeclaration/MonthlyDeclarationPage";
import MonthlyReimbursementEditPage from "containers/MonthlyDeclaration/EditReimbursement/MonthlyReimbursementEditPage";
import MonthlyReimbursementRejectPage from "containers/MonthlyDeclaration/RejectReimbursement/MonthlyReimbursementRejectPage";
import RetrospectiveClaimPage from "containers/RetrospectiveClaim/RetrospectiveClaimPage";
import OfferIssuancePage from "containers/OfferIssuance/OfferIssuance";
import OfferIssuanceApprovePage from "containers/OfferIssuanceApprove/OfferIssuanceApprove";
import CreateRetrospectiveClaim from "containers/CreateRetrospectiveClaim/CreateRetrospectiveClaim";
import ApproveRetrospectiveClaim from "containers/ApproveRetrospectiveClaim/ApproveRetrospectiveClaim";
import ReportGenerationPage from "containers/ReportGeneration/ReportGenerationPage";
import RejectRetrospectiveClaim from "containers/RejectRetrospectiveClaim/RejectRetrospectiveClaim";
import ReviewRetrospectiveClaim from "containers/ReviewRetrospectiveClaim/ReviewRetrospectiveClaim";
import Logout from "containers/Logout/Logout";
import CatchAllRoute from "components/molecules/ErrorPages/CatchAllRoute";
import SpecialApprovalRetrospectiveClaim from "containers/SpecialApprovalRetrospectiveClaim/SpecialApprovalRetrospectiveClaim";
import ReimbursementVerification from "containers/ReimbursementVerification/ReimbursementVerification";
import ReimbursementVerificationReview from "containers/ReimbursementVerificationReview/ReimbursementVerificationReview";
import ReimbursementVerificationRejectClaim from "containers/ReimbursementVerificationRejectClaim/ReimbursementVerificationRejectClaim";
import AccountManagementPage from "containers/AccountManagement/AccountManagementPage";
import CapitaStarDelinkPage from "containers/CapitaStarDelinkPage/CapitaStarDelinkPage";
import KrisFlyerDelinkPage from "containers/KrisFlyerDelinkPage/KrisFlyerDelinkPage";
import CreateAccountPage from "containers/AccountManagement/CreateTenantAccount/CreateAccountPage";
import ReimbursementApprovalReview from "containers/ReimbursementApprovalReview/ReimbursementApprovalReview";
import ViewEditAccountPage from "containers/AccountManagement/EditTenantAccount/ViewEditAccountPage";
import ForgotWatchword from "containers/ForgotWatchword/ForgotWatchword";
import VehicleIUPage from "containers/VehicleIUPage/VehicleIUPage";
import LandingPage from "containers/LandingPage/LandingPage";
import AdhocReportPage from "containers/AdhocReportGeneration/AdhocReportPage";
import MergeCRAccountsPage from "containers/MergeCRAccounts/MergeCRAccountsPage";
import CRPointsExpirySummaryPage from "containers/CRPointsExpirySummary/CRPointsExpirySummaryPage";
import CRMembershipStatusPage from "containers/CRMembershipStatus/CRMembershipStatusPage";
import VoidCRTransactionPage from "containers/VoidCRTransaction/VoidCRTransactionPage";
import OfferRedemptionPage from "containers/OfferRedemption/OfferRedemption";
import OfferUpdatePage from "containers/OfferUpdate/OfferUpdate";
import NonMemberAccountPage from "containers/NonMemberAccount/NonMemberAccount";
import OutletManagementPage from "containers/OutletManagement/OutletManagementPage/OutletManagementPage";
import CreateOutletPage from "containers/OutletManagement/CreateOutletPage/CreateOutletPage";
import EditOutletPage from "containers/OutletManagement/EditOutletPage/EditOutletPage";

export type RouteItem = {
	path: string;
	element: JSX.Element;
	protected: boolean;
	children?: Array<{
		path: string;
		element: JSX.Element;
	}>;
	hide?: boolean;
};
export type ProtectedRoutesMap = {
	admin: { [x: string]: RouteItem };
	tenant: { [x: string]: RouteItem };
};

/**
 * @description This object is to specify protected routes.
 * admin object have all the routes related to admin pages.
 * tenant object have all the routes related to tenant pages.
 * key is equal to perm_code in TBL_PERMISSION_DTL table
 */
export const protectedRoutesMap: ProtectedRoutesMap = {
	admin: {
		CREATE_EDIT_TENANT_ACCOUNT: {
			path: Routes.ACCOUNT_MANAGEMENT,
			element: <Home element={<AccountManagementPage />} />,
			children: [
				{
					path: Routes.CREATE_ACCOUNT,
					element: <CreateAccountPage />
				},
				{
					path: Routes.EDIT_ACCOUNT,
					element: <ViewEditAccountPage />
				}
			],
			protected: true
		},
		REIMB_DECLRATION_VERIFY: {
			path: Routes.DECLARATION_VERIFICATION,
			element: <Home element={<ReimbursementVerification />} />,
			children: [
				{
					path: Routes.REIMBURSEMENT_CLAIM_REVIEW,
					element: <ReimbursementVerificationReview />
				},
				{
					path: Routes.REIMBURSEMENT_REJECT_CLAIM,
					element: <ReimbursementVerificationRejectClaim />
				}
			],
			protected: true
		},
		REIMB_DECLRATION_APPROVE: {
			path: Routes.DECLARATION_APPROVAL,
			element: <Home element={<ReimbursementVerification />} />,
			children: [
				{
					path: Routes.REIMBURSEMENT_CLAIM_REVIEW,
					element: <ReimbursementApprovalReview />
				},
				{
					path: Routes.REIMBURSEMENT_REJECT_CLAIM,
					element: <ReimbursementVerificationRejectClaim />
				}
			],
			protected: true
		},
		RECEIPT_MANAGEMENT: {
			path: Routes.RECEIPT_MANAGEMENT,
			element: <Home element={<p>receipt-management</p>} />,
			protected: true,
			/** @Desc Remove this *hide* attribute to render this path and module */
			hide: true
		},
		REIM_REPORTS_ADMIN: {
			path: Routes.REPORTS_ADMIN,
			element: <Home element={<ReportGenerationPage />} />,
			protected: true
		},
		RETROSPECTIVE_CLAIM: {
			path: Routes.RETRO_CLAIM,
			element: <Home element={<RetrospectiveClaimPage />} />,
			children: [
				{
					path: Routes.CREATE_RETRO_CLAIM,
					element: <CreateRetrospectiveClaim />
				},
				{
					path: Routes.APPROVE_RETRO_CLAIM,
					element: <ApproveRetrospectiveClaim />
				},
				{
					path: Routes.REJECT_RETRO_CLAIM,
					element: <RejectRetrospectiveClaim />
				},
				{
					path: Routes.SPECIAL_APPROVE_CLAIM,
					element: <SpecialApprovalRetrospectiveClaim />
				},
				{
					path: Routes.REVIEW_RETRO_CLAIM,
					element: <ReviewRetrospectiveClaim />
				}
			],
			protected: true
		},
		ROLE_MANAGEMENT: {
			path: Routes.ROLE_MANAGEMENT,
			element: <Home element={<RoleManagementPage />} />,
			children: [
				{
					path: Routes.CREATE_USER,
					element: <NewUser />
				},
				{
					path: Routes.VIEW_EDIT_ADMIN_USER,
					element: <ViewEditUserPage />
				},
				{
					path: Routes.CREATE_GROUP,
					element: <NewGroup />
				},
				{
					path: Routes.EDIT_GROUP,
					element: <EditGroupPage />
				}
			],
			protected: true
		},
		DELINK_CAPITASTAR: {
			path: Routes.DELINK,
			element: <Home element={<CapitaStarDelinkPage />} />,
			protected: true
		},
		DELINK_VEHICLE_IU: {
			path: Routes.VEHICLE_DELINK,
			element: <Home element={<VehicleIUPage />} />,
			protected: true
		},
		DELINK_KRISFLYER: {
			path: Routes.KRISFLYER_DELINK,
			element: <Home element={<KrisFlyerDelinkPage />} />,
			protected: true
		},

		SESSION_M: {
			path: Routes.SESSIONM,
			element: <Home element={<LandingPage />} />,
			protected: true
		},
		ADHOC_REPORTS_ADMIN: {
			path: Routes.REPORT_ADHOC,
			element: <Home element={<AdhocReportPage />} />,
			protected: true
		},

		OFFER_ISSUANCE: {
			path: Routes.OFFER_ISSUANCE,
			element: <Home element={<OfferIssuancePage />} />,
			protected: true
		},
		OFFER_REDEMPTION: {
			path: Routes.OFFER_REDEMPTION,
			element: <Home element={<OfferRedemptionPage />} />,
			protected: true
		},
		OFFER_ISSUANCE_LIST: {
			path: Routes.OFFER_ISSUANCE_APPROVE,
			element: <Home element={<OfferIssuanceApprovePage />} />,
			protected: true
		},
		OFFER_ISSUANCE_CREATE: {
			path: Routes.OFFER_ISSUANCE_CREATE,
			element: <Home element={<OfferIssuancePage />} />,
			protected: true
		},
		OFFER_UPDATE: {
			path: Routes.OFFER_UPDATE,
			element: <Home element={<OfferUpdatePage />} />,
			protected: true
		},
		NONMEMBER_ACCOUNT: {
			path: Routes.NONMEMBER_ACCOUNT,
			element: <Home element={<NonMemberAccountPage />} />,
			protected: true
		},
		MERGE_CR_ACCOUNTS: {
			path: Routes.MERGE_CR_ACCOUNTS,
			element: <Home element={<MergeCRAccountsPage />} />,
			protected: true
		},
		CR_POINTS_EXPIRY_SUMMARY: {
			path: Routes.CR_POINTS_EXPIRY_SUMMARY,
			element: <Home element={<CRPointsExpirySummaryPage />} />,
			protected: true
		},
		CR_MEMBERSHIP_STATUS: {
			path: Routes.CR_MEMBERSHIP_STATUS,
			element: <Home element={<CRMembershipStatusPage />} />,
			protected: true
		},
		VOID_CR_TRANSACTION: {
			path: Routes.VOID_CR_TRANSACTION,
			element: <Home element={<VoidCRTransactionPage />} />,
			protected: true
		},
		OUTLET_MANAGEMENT: {
			path: Routes.OUTLET_MANAGEMENT,
			element: <Home element={<OutletManagementPage />} />,
			protected: true,
			children: [
				{
					path: Routes.CREATE_OUTLET,
					element: <CreateOutletPage />
				},
				{
					path: Routes.EDIT_OUTLET,
					element: <EditOutletPage />
				}
			]
		},
		SETTINGS_VIEW: {
			path: Routes.SYSTEM_SETTINGS,
			element: <Home element={<p>Settings Page - View Mode</p>} />,
			protected: true
		},
		SETTINGS_EDIT: {
			path: Routes.SYSTEM_SETTINGS,
			element: <Home element={<p>Settings Page - Edit Mode</p>} />,
			protected: true
		}
	},
	tenant: {
		REIM_MONTHLY_DECLARATION: {
			path: Routes.MONTHLY_DECLARATIONS,
			element: <Home element={<MonthlyDeclarationPage />} />,
			children: [
				{
					path: Routes.MONTHLY_DECLARATIONS_EDIT,
					element: <MonthlyReimbursementEditPage />
				},
				{
					path: Routes.MONTHLY_DECLARATIONS_REJECT,
					element: <MonthlyReimbursementRejectPage />
				}
			],
			protected: true
		},
		REIM_REPORTS_TENANT: {
			path: Routes.REPORTS_TENANT,
			element: <Home element={<ReportGenerationPage />} />,
			protected: true
		}
	}
};

/**
 * @description This object includes all the paths which can be accessed without authorization.
 */
export const routes: RouteObject[] = [
	{
		path: Routes.HOME,
		element: (
			<RouteGuard>
				<Home element={<p>Home</p>} />
			</RouteGuard>
		)
	},
	{
		path: Routes.LOGOUT,
		element: <Logout />
	},
	{
		path: Routes.LOGIN,
		element: <Login />
	},
	{
		path: Routes.FORGOT_WATCHWORD,
		element: <ForgotWatchword />
	},
	{
		path: Routes.SUCCESS,
		element: <Alert />
	},
	{
		path: "*",
		element: <CatchAllRoute />
	}
];
